import { Link } from 'react-router-dom';
import { categorySegment } from '../../types/conceptualProblems';
import type { ConceptualCategory } from '../../types/conceptualProblems';

interface ConceptualCategoryCardProps {
  category: ConceptualCategory;
}

/**
 * One topic on the conceptual-problem category list (feature 029).
 *
 * <p>Deliberately the same card language as the Data Editing landing page an editor has just come
 * from — same panel, same border, same "Open →" affordance — so the two lists read as one journey
 * rather than two designs.</p>
 *
 * <p>A category holding nothing still gets a card, and still opens: an editor needs to see that
 * the topic exists and is empty. It is a link rather than a button so it can be opened in a new
 * tab, which is how these get compared side by side.</p>
 */
export function ConceptualCategoryCard({ category }: ConceptualCategoryCardProps) {
  const count = category.problemCount;

  return (
    <Link
      to={`/conceptual-problems/${categorySegment(category.id)}`}
      className="block w-full text-left p-5 bg-card rounded-lg shadow hover:shadow-md transition border border-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass"
      data-testid={`conceptual-category-${categorySegment(category.id)}`}
    >
      <h2 className="text-lg font-semibold text-ink mb-1 break-words">{category.name}</h2>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm text-ink-soft">
          {count} {count === 1 ? 'problem' : 'problems'}
        </p>
        <span className="text-sm font-medium text-primary-700 whitespace-nowrap">Open →</span>
      </div>
    </Link>
  );
}
